import { Instagram, Facebook } from 'lucide-react';
import type { StoryPartData, StorySlideTemplate } from './types';

// ─── Content Type Definitions ────────────────────────────────────────

export const contentTypeOptions = [
  {
    id: 'post' as const,
    label: 'Post',
    description: 'Standardowy post na social media',
    iconName: 'FileText',
    color: 'bg-blue-600',
    hoverColor: 'hover:bg-blue-700',
    borderColor: 'border-blue-500',
    bgLight: 'bg-blue-50',
    textColor: 'text-blue-700',
  },
  {
    id: 'reel' as const,
    label: 'Reel',
    description: 'Krótkie wideo w formacie pionowym (9:16)',
    iconName: 'Film',
    color: 'bg-purple-600',
    hoverColor: 'hover:bg-purple-700',
    borderColor: 'border-purple-500',
    bgLight: 'bg-purple-50',
    textColor: 'text-purple-700',
  },
  {
    id: 'story' as const,
    label: 'Story',
    description: 'Historia — jedno- lub wieloelementowa',
    iconName: 'Layers',
    color: 'bg-gradient-to-r from-pink-500 to-orange-500',
    hoverColor: 'hover:from-pink-600 hover:to-orange-600',
    borderColor: 'border-pink-500',
    bgLight: 'bg-pink-50',
    textColor: 'text-pink-700',
  },
];

// ─── Platforms supporting Reels/Stories ──────────────────────────────

export const reelsStoryPlatforms = [
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'bg-blue-600' },
  { id: 'instagram', name: 'Instagram (via Facebook)', icon: Instagram, color: 'bg-pink-600' },
  { id: 'instagram_direct', name: 'Instagram Direct', icon: Instagram, color: 'bg-purple-600' },
];

// ─── Media Constraints ──────────────────────────────────────────────

// Reels
export const REELS_MAX_DURATION_SECONDS = 90;
export const REELS_MIN_DURATION_SECONDS = 3;
export const REELS_MAX_FILE_SIZE_MB = 1024; // 1 GB
export const REELS_RECOMMENDED_ASPECT_RATIO = '9:16';
export const REELS_SUPPORTED_VIDEO_FORMATS = ['mp4', 'mov'];
export const REELS_MAX_CAPTION_LENGTH = 2200;

// Stories
export const STORY_MAX_DURATION_SECONDS = 60;
export const STORY_SLIDE_DEFAULT_DURATION = 15;
export const STORY_MAX_SLIDES = 10;
export const STORY_MAX_FILE_SIZE_MB = 100;
export const STORY_IMAGE_RECOMMENDED_SIZE = '1080x1920';
export const STORY_SUPPORTED_IMAGE_FORMATS = ['jpg', 'jpeg', 'png'];
export const STORY_SUPPORTED_VIDEO_FORMATS = ['mp4', 'mov'];

// Auto-split content settings
export const STORY_AUTO_SPLIT_MAX_CHARS = 250;
export const STORY_AUTO_SPLIT_SEPARATOR = '\n\n';

// ─── Slide Templates ────────────────────────────────────────────────

export interface SlideTemplateConfig {
  id: StorySlideTemplate;
  label: string;
  description: string;
  iconName: string;
  borderColor: string;
  bgColor: string;
  textColor: string;
  badgeColor: string;
}

export const STORY_SLIDE_TEMPLATES: SlideTemplateConfig[] = [
  {
    id: 'text',
    label: 'Tekst',
    description: 'Standardowy tekst',
    iconName: 'Type',
    borderColor: 'border-gray-300',
    bgColor: 'bg-white',
    textColor: 'text-gray-700',
    badgeColor: 'bg-gray-100 text-gray-600',
  },
  {
    id: 'title',
    label: 'Tytuł',
    description: 'Nagłówek / Hook',
    iconName: 'Heading',
    borderColor: 'border-indigo-400',
    bgColor: 'bg-indigo-50',
    textColor: 'text-indigo-700',
    badgeColor: 'bg-indigo-100 text-indigo-700',
  },
  {
    id: 'quote',
    label: 'Cytat',
    description: 'Wyróżnione zdanie',
    iconName: 'Quote',
    borderColor: 'border-amber-400',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    badgeColor: 'bg-amber-100 text-amber-700',
  },
  {
    id: 'cta',
    label: 'CTA',
    description: 'Call-to-Action',
    iconName: 'MousePointerClick',
    borderColor: 'border-emerald-400',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    badgeColor: 'bg-emerald-100 text-emerald-700',
  },
  {
    id: 'poll',
    label: 'Ankieta',
    description: 'Pytanie / Quiz',
    iconName: 'BarChart3',
    borderColor: 'border-violet-400',
    bgColor: 'bg-violet-50',
    textColor: 'text-violet-700',
    badgeColor: 'bg-violet-100 text-violet-700',
  },
  {
    id: 'media',
    label: 'Media',
    description: 'Tylko zdjęcie/wideo',
    iconName: 'Image',
    borderColor: 'border-pink-400',
    bgColor: 'bg-pink-50',
    textColor: 'text-pink-700',
    badgeColor: 'bg-pink-100 text-pink-700',
  },
];

export function getSlideTemplateConfig(template?: StorySlideTemplate): SlideTemplateConfig {
  return STORY_SLIDE_TEMPLATES.find(t => t.id === template) || STORY_SLIDE_TEMPLATES[0];
}

// ─── AI Response Parser ─────────────────────────────────────────────

/**
 * Detects slide template from text content using tag markers and heuristics.
 * Tags: [TYTUŁ], [CYTAT], [CTA], [ANKIETA]
 */
export function detectSlideTemplate(text: string, index: number, total: number): StorySlideTemplate {
  const trimmed = text.trim().toUpperCase();

  // Explicit tags
  if (trimmed.startsWith('[TYTUŁ]') || trimmed.startsWith('[TYTUL]')) return 'title';
  if (trimmed.startsWith('[CYTAT]')) return 'quote';
  if (trimmed.startsWith('[CTA]')) return 'cta';
  if (trimmed.startsWith('[ANKIETA]') || trimmed.startsWith('[POLL]')) return 'poll';
  if (trimmed.startsWith('[MEDIA]')) return 'media';

  // Heuristic detection
  if (index === 0) return 'title'; // First slide is usually a title
  if (index === total - 1 && (trimmed.includes('KLIKNIJ') || trimmed.includes('LINK') || trimmed.includes('SWIPE') || trimmed.includes('SPRAWDŹ') || trimmed.includes('ZAPISZ') || trimmed.includes('OBSERWUJ'))) return 'cta';
  if (text.includes('"') && text.includes('"') || text.includes('„') || text.includes('"')) return 'quote';
  if (/[AB]\)|\bczy\b.*\?/i.test(text)) return 'poll';

  return 'text';
}

/**
 * Strips template tag from the beginning of text content.
 */
function stripTemplateTag(text: string): string {
  return text.replace(/^\[(TYTUŁ|TYTUL|CYTAT|CTA|ANKIETA|POLL|MEDIA)\]\s*/i, '').trim();
}

/**
 * Parses AI-generated Story response into structured slide data.
 * Splits by "---" separator and auto-detects template types.
 * Returns null if the response doesn't contain valid slides.
 */
export function parseAIStoryResponse(aiResponse: string): StoryPartData[] | null {
  if (!aiResponse || !aiResponse.trim()) return null;

  const trimmed = aiResponse.trim();

  // Split by --- separator (with optional whitespace around it)
  const rawSlides = trimmed.split(/\n\s*---\s*\n/).filter(s => s.trim().length > 0);

  // If no separator found or only 1 block, not a multi-slide response
  if (rawSlides.length <= 1) return null;

  // Cap at max slides
  const slides = rawSlides.slice(0, STORY_MAX_SLIDES);

  return slides.map((raw, index) => {
    const text = raw.trim();
    const template = detectSlideTemplate(text, index, slides.length);
    const cleanText = stripTemplateTag(text);

    return {
      partOrder: index + 1,
      content: cleanText,
      mediaUrl: null,
      mediaType: 'image' as const,
      durationSeconds: STORY_SLIDE_DEFAULT_DURATION,
      template,
    };
  });
}

// ─── Helper functions ───────────────────────────────────────────────

/**
 * Checks if a platform supports Reels content type
 */
export function platformSupportsReels(platform: string): boolean {
  return ['facebook', 'instagram', 'instagram_direct'].includes(platform);
}

/**
 * Checks if a platform supports Stories content type
 */
export function platformSupportsStories(platform: string): boolean {
  return ['facebook', 'instagram', 'instagram_direct'].includes(platform);
}

/**
 * Auto-splits long text content into story segments.
 * Priority: 1) --- separators, 2) paragraphs, 3) sentences, 4) word boundaries.
 * Each segment stays under the max character limit.
 */
export function autoSplitContentForStory(
  content: string,
  maxCharsPerSlide: number = STORY_AUTO_SPLIT_MAX_CHARS,
  maxSlides: number = STORY_MAX_SLIDES
): string[] {
  if (!content || content.trim().length === 0) return [''];

  const trimmed = content.trim();

  // Priority 1: Split by --- separator (AI format)
  const dashSplits = trimmed.split(/\n\s*---\s*\n/).filter(s => s.trim().length > 0);
  if (dashSplits.length > 1) {
    return dashSplits
      .map(s => stripTemplateTag(s.trim()))
      .slice(0, maxSlides);
  }

  // If content fits in one slide, return as is
  if (trimmed.length <= maxCharsPerSlide) {
    return [trimmed];
  }

  // Priority 2: Split by paragraphs
  const paragraphs = trimmed.split(/\n\n+/).filter(p => p.trim().length > 0);
  const segments: string[] = [];
  let currentSegment = '';

  for (const paragraph of paragraphs) {
    if (paragraph.length > maxCharsPerSlide) {
      // Paragraph is too long — split by sentences
      if (currentSegment.trim()) {
        segments.push(currentSegment.trim());
        currentSegment = '';
      }

      const sentences = paragraph.match(/[^.!?]+[.!?]+\s*/g) || [paragraph];
      for (const sentence of sentences) {
        if ((currentSegment + sentence).length > maxCharsPerSlide) {
          if (currentSegment.trim()) {
            segments.push(currentSegment.trim());
          }
          // If a single sentence is longer than maxChars, force-split by words
          if (sentence.length > maxCharsPerSlide) {
            const words = sentence.split(/\s+/);
            currentSegment = '';
            for (const word of words) {
              if ((currentSegment + ' ' + word).trim().length > maxCharsPerSlide) {
                if (currentSegment.trim()) segments.push(currentSegment.trim());
                currentSegment = word;
              } else {
                currentSegment = currentSegment ? currentSegment + ' ' + word : word;
              }
            }
          } else {
            currentSegment = sentence;
          }
        } else {
          currentSegment += sentence;
        }
      }
    } else if ((currentSegment + '\n\n' + paragraph).length > maxCharsPerSlide) {
      // Adding this paragraph would exceed limit
      if (currentSegment.trim()) {
        segments.push(currentSegment.trim());
      }
      currentSegment = paragraph;
    } else {
      currentSegment = currentSegment ? currentSegment + '\n\n' + paragraph : paragraph;
    }
  }

  if (currentSegment.trim()) {
    segments.push(currentSegment.trim());
  }

  // Limit to max slides
  return segments.slice(0, maxSlides);
}

/**
 * Returns badge label for content type display
 */
export function getContentTypeBadgeLabel(contentType: string): string {
  switch (contentType) {
    case 'reel': return 'Reel';
    case 'story': return 'Story';
    default: return 'Post';
  }
}

/**
 * Returns color classes for content type badge
 */
export function getContentTypeBadgeColors(contentType: string): { bg: string; text: string } {
  switch (contentType) {
    case 'reel':
      return { bg: 'bg-purple-100', text: 'text-purple-700' };
    case 'story':
      return { bg: 'bg-pink-100', text: 'text-pink-700' };
    default:
      return { bg: 'bg-blue-100', text: 'text-blue-700' };
  }
}

